import React, { useState, useMemo } from "react";
import { Popover, PopoverTrigger, PopoverContent, Button, Input } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

interface IconOption {
  name: string;
  label: string;
}

// Curated list of icons suitable for prompt shortcuts
const SHORTCUT_ICONS: IconOption[] = [
  { name: "solar:star-outline", label: "Star" },
  { name: "solar:star-bold", label: "Star Filled" },
  { name: "solar:bolt-outline", label: "Bolt" },
  { name: "solar:chat-round-outline", label: "Chat" },
  { name: "solar:document-text-outline", label: "Document" },
  { name: "solar:code-square-outline", label: "Code" },
  { name: "solar:chart-outline", label: "Chart" },
  { name: "solar:calculator-outline", label: "Calculator" },
  { name: "solar:clipboard-text-outline", label: "Clipboard" },
  { name: "solar:folder-outline", label: "Folder" },
  { name: "solar:magnifer-outline", label: "Search" },
  { name: "solar:pen-outline", label: "Pen" },
  { name: "solar:lightbulb-outline", label: "Lightbulb" },
  { name: "solar:lightbulb-bolt-outline", label: "Idea" },
  { name: "solar:bookmark-outline", label: "Bookmark" },
  { name: "solar:calendar-outline", label: "Calendar" },
  { name: "solar:clock-circle-outline", label: "Clock" },
  { name: "solar:link-outline", label: "Link" },
  { name: "solar:settings-outline", label: "Settings" },
  { name: "solar:user-outline", label: "User" },
  { name: "solar:users-group-rounded-outline", label: "Team" },
  { name: "solar:heart-outline", label: "Heart" },
  { name: "solar:flag-outline", label: "Flag" },
  { name: "solar:bell-outline", label: "Bell" },
  { name: "solar:mail-outline", label: "Mail" },
  { name: "solar:planet-outline", label: "Planet" },
  { name: "solar:rocket-outline", label: "Rocket" },
  { name: "solar:shield-check-outline", label: "Shield" },
  { name: "solar:wallet-outline", label: "Wallet" },
  { name: "solar:graph-up-outline", label: "Growth" },
];

interface IconPickerProps {
  value: string;
  onChange: (icon: string) => void;
  placeholder?: string;
  "aria-label"?: string;
}

export const IconPicker: React.FC<IconPickerProps> = ({
  value,
  onChange,
  placeholder,
  "aria-label": ariaLabel,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredIcons = useMemo(() => {
    if (!searchQuery.trim()) {
      return SHORTCUT_ICONS;
    }
    const query = searchQuery.toLowerCase();

    return SHORTCUT_ICONS.filter(
      (icon) => icon.label.toLowerCase().includes(query) || icon.name.toLowerCase().includes(query),
    );
  }, [searchQuery]);

  const selectedIconLabel = useMemo(() => {
    const found = SHORTCUT_ICONS.find((icon) => icon.name === value);

    return found?.label || "";
  }, [value]);

  const handleSelectIcon = (iconName: string) => {
    onChange(iconName);
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleClearIcon = () => {
    onChange("");
    setIsOpen(false);
  };

  return (
    <Popover isOpen={isOpen} placement="bottom-start" onOpenChange={setIsOpen}>
      <PopoverTrigger>
        <Button
          aria-label={ariaLabel}
          className="w-full justify-start h-auto min-h-10 py-2"
          data-testid="icon-picker-trigger"
          variant="bordered"
        >
          <div className="flex items-center gap-2 w-full">
            {value ? (
              <>
                <Icon className="text-primary" height={20} icon={value} width={20} />
                <span className="text-sm">{selectedIconLabel || value}</span>
              </>
            ) : (
              <span className="text-default-400 text-sm">
                {placeholder || t("Select an icon (optional)")}
              </span>
            )}
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0">
        <div className="p-3 space-y-3">
          {/* Search Input */}
          <Input
            classNames={{
              inputWrapper: "h-9",
            }}
            data-testid="icon-search-input"
            placeholder={t("Search icons...")}
            size="sm"
            startContent={<Icon className="text-default-400" icon="solar:magnifer-outline" />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          {/* Icon Grid */}
          <div className="grid grid-cols-5 gap-1 max-h-48 overflow-y-auto" data-testid="icon-grid">
            {filteredIcons.map((icon) => (
              <button
                key={icon.name}
                aria-label={icon.label}
                className={`p-2 rounded-lg hover:bg-default-100 transition-colors flex flex-col items-center gap-1 ${
                  value === icon.name ? "bg-primary/10 ring-1 ring-primary" : ""
                }`}
                data-testid={`icon-option-${icon.name}`}
                title={icon.label}
                type="button"
                onClick={() => handleSelectIcon(icon.name)}
              >
                <Icon height={24} icon={icon.name} width={24} />
                <span className="text-[10px] text-default-500 truncate w-full text-center">
                  {icon.label}
                </span>
              </button>
            ))}
          </div>

          {filteredIcons.length === 0 && (
            <p className="text-center text-default-400 text-sm py-4" data-testid="no-icons-message">
              {t("No icons found")}
            </p>
          )}

          {/* Clear Button */}
          {value && (
            <Button
              className="w-full"
              color="danger"
              data-testid="icon-clear-button"
              size="sm"
              variant="light"
              onClick={handleClearIcon}
            >
              {t("Clear icon")}
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export { SHORTCUT_ICONS };
export type { IconOption, IconPickerProps };
